import React from "react";
import { connect } from "react-redux";
import { Form, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";

import ConditionalButton from "../../../layout/conditional-button";
import ErrorModal from "../../../layout/modal";
import DeleteModal from "../../../layout/modal";

import { isGuid } from "../../../utils/string";
import { formatDateOnly, parseDateOnly } from "../../../utils/date";
import { withNavigation } from "../../../utils/wrapper";

import { send as pageInfo, loading } from "../../../actions/page";

import {
  get as getPayPeriod,
  save as savePayPeriod,
  destroy as destroyPayPeriod,
} from "../../../actions/pay.periods";

import { get as getPaySchedules } from "../../../actions/pay.schedules";

const STATUS_VALUES = ["draft", "open", "processing", "closed", "paid", "void"];

class Detail extends React.Component {
  constructor(props) {
    super(props);
    this.props.loading(true);
    this.state = {
      data: this.mapPropsToState(props),
      schedules: [],
      modal: {
        error: { show: false },
        delete: { show: false },
      },
    };

    this.props.pageInfo({ title: "Pay Period", breadcrumb: `[New Pay Period]` });
    this.changes = {};
  }

  mapPropsToState = (props) => {
    const {
      uuid,
      payScheduleUuid,
      startsOn,
      endsOn,
      payDate,
      label,
      status,
      lockedAt,
      lockedByUuid,
      approvedAt,
      approvedByUuid,
    } = props;

    return {
      uuid,
      payScheduleUuid,
      startsOn,
      endsOn,
      payDate,
      label,
      status,
      lockedAt,
      lockedByUuid,
      approvedAt,
      approvedByUuid,
    };
  };

  get uuid() {
    return this.props.params.uuid;
  }

  loadSchedules = async () => {
    const schedules = await this.props.getPaySchedules(null);
    this.setState({ schedules: schedules || [] });
  };

  loadData = async () => {
    this.props.loading(true);

    if (isGuid(this.uuid)) {
      const items = await this.props.getPayPeriod(this.uuid);
      const item = items.items?.[0];

      this.setState({ data: { ...item } });

      const caption =
        item?.label ||
        `${String(item?.startsOn ?? "").slice(0, 10)} - ${String(item?.endsOn ?? "").slice(0, 10)}`;

      await this.props.pageInfo({ title: `Pay Period (${caption})`, breadcrumb: `${caption}` });
    }

    this.props.loading(false);
  };

  componentDidMount = () => {
    setTimeout(async () => {
      await this.loadSchedules();
      await this.loadData();
    }, 30);
  };

  componentDidUpdate = (oldProps) => {
    if (oldProps?.params?.uuid != this.props?.params?.uuid) this.componentDidMount();
  }

  doPostChange = () => {
    if (this.props.onChange) this.props.onChange(this.state);
  };

  onChange = (e) => {
    this.setState(
      (prev) => ({
        data: {
          ...prev.data,
          [e.target.name]: e.target.value,
        },
      }),
      this.doPostChange
    );
  };

  maybeAutoLabel = () => {
    const d = this.state.data || {};
    if (d.label) return;

    const s = d.startsOn ? String(d.startsOn).slice(0, 10) : "";
    const e = d.endsOn ? String(d.endsOn).slice(0, 10) : "";
    if (s && e) {
      this.setState(
        (prev) => ({
          data: { ...prev.data, label: `${s}—${e}` },
        }),
        this.doPostChange
      );
    }
  };

  // ---- react-select helpers (Schedule)
  get scheduleOptions() {
    const schedules = this.state.schedules || [];
    return schedules.map((s) => ({
      value: s.uuid,
      label: s?.caption || s?.name || s?.label || s?.code || s?.uuid,
    }));
  }

  get selectedScheduleOption() {
    const uuid = this.state.data?.payScheduleUuid;
    if (!uuid) return null;
    return this.scheduleOptions.find((o) => o.value === uuid) || null;
  }

  onScheduleChange = (opt) => {
    const value = opt?.value ?? "";
    this.onChange({ target: { name: "payScheduleUuid", value } });
  };

  // ---- react-select helpers (Status)
  get statusOptions() {
    return STATUS_VALUES.map((s) => ({
      value: s,
      label: s.toUpperCase(),
    }));
  }

  get selectedStatusOption() {
    const value = this.state.data?.status || "draft";
    return this.statusOptions.find((o) => o.value === value) || this.statusOptions[0];
  }

  onStatusChange = (opt) => {
    const value = opt?.value ?? "draft";
    this.onChange({ target: { name: "status", value } });
  };

  onSaveClick = async () => {
    this.props.loading(true);

    const payload = {
      ...this.state.data,
      uuid: isGuid(this.uuid) ? this.uuid : uuidv4(),
      payScheduleUuid: isGuid(this.state.data?.payScheduleUuid)
        ? this.state.data.payScheduleUuid
        : null,
      status: this.state.data?.status || "draft",
      startsOn: formatDateOnly(this.state.data?.startsOn),
      endsOn: formatDateOnly(this.state.data?.endsOn),
      payDate: formatDateOnly(this.state.data?.payDate),
    };

    const saved = await this.props.savePayPeriod(payload);
    const id = saved?.uuid ?? payload.uuid;

    window.location.href = `/dashboard/accounting/payperiods/${id}`;
  };

  onDeleteClick = () => {
    const caption =
      this.state.data?.label ||
      `${String(this.state.data?.startsOn ?? "").slice(0, 10)} - ${String(this.state.data?.endsOn ?? "").slice(0, 10)}`;

    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        delete: {
          title: "Delete pay period",
          body: `This action will permanently delete the pay period ${caption}.`,
          show: true,
        },
      },
    }));
  };

  onDeleteOkModal = async () => {
    await this.props.destroyPayPeriod(this.state.data?.uuid || this.uuid);
    this.onDeleteHideModal();
    this.props.navigate("/dashboard/accounting/payperiods");
  };

  onDeleteHideModal = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        delete: { show: false },
      },
    }));
  };

  onErrorHideModal = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        error: { show: false },
      },
    }));
  };

  render() {
    const d = this.state.data || {};

    return (
      <>
        <Col md="8" className="float-right text-end">
          <ConditionalButton
            enabledVariant="outline-primary"
            tooltip="Save Pay Period"
            display={true}
            enabled={true}
            onEnabledClick={this.onSaveClick}
            icon="save"
          >
            Save
          </ConditionalButton>

          <ConditionalButton
            enabledVariant="outline-primary"
            tooltip="Delete Pay Period"
            display={true}
            enabled={isGuid(this.uuid)}
            onEnabledClick={this.onDeleteClick}
            icon="bin"
          >
            Delete
          </ConditionalButton>
        </Col>

        <Col xs="12">
          <Row className="">
            <Col xs="12" md="8" xl="4">
              <div className="mb-3 top-label">
                <Form.Control
                  type="text"
                  name="label"
                  value={d?.label || ""}
                  onChange={this.onChange}
                  placeholder="e.g., 2026-01-01—2026-01-15"
                />
                <Form.Label>LABEL</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="4">
              <div className="mb-3 top-label">
                <Select
                  inputId="status"
                  classNamePrefix="react-select"
                  isClearable={false}
                  options={this.statusOptions}
                  value={this.selectedStatusOption}
                  onChange={this.onStatusChange}
                />
                <Form.Label>STATUS</Form.Label>
              </div>
            </Col>

            <Col xs="12" xl="4">
              <div className="mb-3 top-label">
                <Select
                  inputId="payScheduleUuid"
                  classNamePrefix="react-select"
                  isClearable
                  placeholder="(No schedule)"
                  options={this.scheduleOptions}
                  value={this.selectedScheduleOption}
                  onChange={this.onScheduleChange}
                />
                <Form.Label>PAY SCHEDULE</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="4">
              <div className="mb-3 top-label">
                <DatePicker
                  id="startsOn"
                  name="startsOn"
                  className="form-control"
                  selected={parseDateOnly(d?.startsOn)}
                  onChange={(date) => {
                    this.onChange({ target: { name: "startsOn", value: formatDateOnly(date) } });
                    setTimeout(this.maybeAutoLabel, 0);
                  }}
                />
                <Form.Label>STARTS ON</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="4">
              <div className="mb-3 top-label">
                <DatePicker
                  id="endsOn"
                  name="endsOn"
                  className="form-control"
                  selected={parseDateOnly(d?.endsOn)}
                  onChange={(date) => {
                    this.onChange({ target: { name: "endsOn", value: formatDateOnly(date) } });
                    setTimeout(this.maybeAutoLabel, 0);
                  }}
                />
                <Form.Label>ENDS ON</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="4">
              <div className="mb-3 top-label">
                <DatePicker
                  isClearable
                  id="payDate"
                  name="payDate"
                  className="form-control"
                  selected={parseDateOnly(d?.payDate)}
                  onChange={(date) => this.onChange({ target: { name: "payDate", value: formatDateOnly(date) } })}
                />
                <Form.Label>PAY DATE</Form.Label>
              </div>
            </Col>
          </Row>
        </Col>

        <ErrorModal
          show={this.state.modal.error.show}
          title={this.state.modal.error.title}
          body={this.state.modal.error.body}
          buttons={[
            {
              default: true,
              caption: "ok",
              onEnabledClick: this.onErrorHideModal,
              variant: "secondary",
              display: true,
              enabled: true,
            },
          ]}
        ></ErrorModal>

        <DeleteModal
          show={this.state.modal.delete.show}
          title={this.state.modal.delete.title}
          body={this.state.modal.delete.body}
          buttons={[
            {
              caption: "ok",
              onEnabledClick: this.onDeleteOkModal,
              variant: "primary",
              display: true,
              enabled: true,
            },
            {
              default: true,
              caption: "cancel",
              onEnabledClick: this.onDeleteHideModal,
              variant: "secondary",
              display: true,
              enabled: true,
            },
          ]}
        ></DeleteModal>
      </>
    );
  }
}

Detail.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  };
};

export default withNavigation(
  connect(mapStateToProps, {
    pageInfo,
    loading,
    getPayPeriod,
    savePayPeriod,
    destroyPayPeriod,
    getPaySchedules,
  })(Detail)
);
